import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { propOr } from 'ramda'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import { getProgramme, reviewObligationEvidence } from 'services/checklistService'
import { RAG as RAG_LEVELS, scoreColour } from 'features/dashboard/charts/scoreColour'
import { criticalityTint, criticalityLabel } from 'features/compliance/obligationConstants'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { line, radius, font, surface, cardStyle, transition } from 'features/dashboard/tokens'
import SharedBackLink from 'components/BackLink'

const UNIT_STATE_META = {
  covered:   { label: 'Covered',         colour: RAG_LEVELS.low.fg,      Icon: VerifiedRoundedIcon },
  in_review: { label: 'Awaiting review', colour: RAG_LEVELS.medium.fg,   Icon: RateReviewRoundedIcon },
  pending:   { label: 'Awaiting evidence', colour: RAG_LEVELS.medium.fg, Icon: ScheduleRoundedIcon },
  expired:   { label: 'Expired',         colour: RAG_LEVELS.critical.fg, Icon: ErrorOutlineRoundedIcon },
  denied:    { label: 'Denied',          colour: RAG_LEVELS.critical.fg, Icon: BlockRoundedIcon },
}

const STATUS_META = {
  satisfied: { label: 'Satisfied',        colour: RAG_LEVELS.low.fg,      Icon: VerifiedRoundedIcon },
  in_review: { label: 'Awaiting review',  colour: RAG_LEVELS.medium.fg,   Icon: RateReviewRoundedIcon },
  pending:   { label: 'Awaiting evidence', colour: RAG_LEVELS.medium.fg,  Icon: ScheduleRoundedIcon },
  expired:   { label: 'Expired',          colour: RAG_LEVELS.critical.fg, Icon: ErrorOutlineRoundedIcon },
  overdue:   { label: 'Overdue',          colour: RAG_LEVELS.critical.fg, Icon: ErrorOutlineRoundedIcon },
  skipped:   { label: 'Not applicable',   colour: RAG_LEVELS.none.fg,     Icon: BlockRoundedIcon },
}

const critLevelOf = o => {
  if (o.criticality) return String(o.criticality).toLowerCase()
  const w = o.criticalityWeight ?? 1
  return w >= 4 ? 'critical' : w === 3 ? 'high' : w === 2 ? 'medium' : 'low'
}

const critLabelOf = o =>
  o.criticality
    ? o.criticality.charAt(0).toUpperCase() + o.criticality.slice(1)
    : criticalityLabel(o.criticalityWeight)

const formatDate = iso => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const UnitComplianceView = ({ bpId, unitId, unitName, bpName, onBack }) => {
  const navigate = useNavigate()
  const [programme, setProgramme] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reviewBusy, setReviewBusy] = useState(null)
  const [denyTarget, setDenyTarget] = useState(null)
  const [denyReason, setDenyReason] = useState('')

  const load = useCallback(({ silent = false } = {}) => {
    if (!bpId) { setLoading(false); return }
    if (!silent) setLoading(true)
    getProgramme(bpId)
      .then(res => {
        setProgramme(propOr(null, 'data', res.data))
        setLoading(false)
      })
      .catch(() => {
        toast.error("Couldn't load this Unit Passport's compliance.")
        setLoading(false)
      })
  }, [bpId])

  useEffect(() => { load() }, [load])

  const { unitObligations, buildingObligations } = useMemo(() => {
    const domains = propOr([], 'domains', programme)
    const unitRows = []
    const buildingRows = []
    domains.forEach(dom => {
      ;(dom.obligations || []).forEach(o => {
        if (o.isSkipped) return
        if (o.scope === 'unit') {
          const units = o.unitCoverage?.units || []
          const entry = units.find(u => (u.unitId || u.unit_id) === unitId)
          if (!entry) return
          unitRows.push({ obligation: o, domain: dom, entry })
        } else {
          buildingRows.push({ obligation: o, domain: dom })
        }
      })
    })
    return { unitObligations: unitRows, buildingObligations: buildingRows }
  }, [programme, unitId])

  const unitScore = useMemo(() => {
    const total = unitObligations.length
    if (total === 0) return null
    const covered = unitObligations.filter(r => (r.entry.state || 'pending') === 'covered').length
    return Math.round((covered / total) * 100)
  }, [unitObligations])

  const unitHasEvidence = useMemo(
    () => unitObligations.some(r => (r.entry.state || 'pending') !== 'pending'),
    [unitObligations]
  )
  const unitScoreDisplay = unitObligations.length > 0 && unitHasEvidence ? unitScore : null

  const approveUnit = (obligation, entry) => {
    const uid = entry.unitId || entry.unit_id
    const fileId = entry.fileId || entry.file_id
    const key = `${obligation.id}:${uid}`
    if (reviewBusy) return
    setReviewBusy(key)
    reviewObligationEvidence(bpId, obligation.id, { verdict: 'approved', unitId: uid, fileId })
      .then(() => { toast.success('Unit evidence approved.'); load({ silent: true }) })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not approve this evidence.') })
      .finally(() => setReviewBusy(null))
  }

  const denyUnit = (obligation, entry, reason) => {
    const uid = entry.unitId || entry.unit_id
    const fileId = entry.fileId || entry.file_id
    const key = `${obligation.id}:${uid}`
    if (reviewBusy) return
    setReviewBusy(key)
    reviewObligationEvidence(bpId, obligation.id, { verdict: 'denied', unitId: uid, fileId, denialReason: reason || null })
      .then(() => { toast.success('Unit evidence denied. This obligation is back to awaiting evidence.'); load({ silent: true }) })
      .catch(e => { toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not deny this evidence.') })
      .finally(() => setReviewBusy(null))
  }

  const openBp = () => navigate(`/bp-list/${bpId}/compliance`)
  const goBackToUnits = onBack || (bpId ? () => navigate(`/dashboard?altitude=unit&entityId=${bpId}`) : null)

  return (
    <Wrap>
      {goBackToUnits && (
        <BackLink
          onClick={goBackToUnits}
          title={`Back to all Unit Passports on ${bpName || 'this Building Passport'}`}
        >
          All Unit Passports{bpName ? ` on ${bpName}` : ''}
        </BackLink>
      )}
      <Header>
        <HeaderLeft>
          <Eyebrow>Unit Passport compliance</Eyebrow>
          <Title>
            {unitName || 'Unit Passport'}
            {bpName ? <TitleSub> · {bpName}</TitleSub> : null}
          </Title>
        </HeaderLeft>
        <HeaderRight>
          {unitObligations.length > 0 && (
            unitScoreDisplay !== null ? (
              <ScorePill $tint={scoreColour(unitScoreDisplay)} title='Share of this unit’s obligations currently covered.'>
                {unitScoreDisplay}%
              </ScorePill>
            ) : (
              <NotScoredPill title='No evidence has been added for this unit yet. Its score appears once the first document is uploaded and approved.'>
                Not yet scored
              </NotScoredPill>
            )
          )}
          <ComplianceScoreExplainer align='right' />
          <OpenBpBtn type='button' onClick={openBp} title={`Open ${bpName || 'the Building Passport'}’s full compliance tracker`}>
            <LaunchRoundedIcon style={{ fontSize: 15 }} /> Open Building Passport ↗
          </OpenBpBtn>
        </HeaderRight>
      </Header>

      {loading ? (
        <SkeletonWrap aria-hidden='true'>
          {[0, 1].map(s => (
            <SkeletonSection key={s}>
              <SkeletonLine $w='180px' $h='13px' />
              {[0, 1, 2].map(r => (
                <SkeletonCard key={r}>
                  <SkeletonLine $w='60%' $h='13px' />
                  <SkeletonLine $w='38%' $h='10px' />
                  <SkeletonLine $w='100%' $h='30px' $r='8px' />
                </SkeletonCard>
              ))}
            </SkeletonSection>
          ))}
        </SkeletonWrap>
      ) : (unitObligations.length === 0 && buildingObligations.length === 0) ? (
        <Empty>
          No obligations apply to this Unit Passport yet. Obligations come from its Building
          Passport’s compliance programme. Set that up to start tracking this unit.
          <EmptyAction type='button' onClick={openBp}>Open Building Passport ↗</EmptyAction>
        </Empty>
      ) : (
        <>
          <Section>
            <SectionHead>
              <SectionEyebrow>Unit-specific obligations</SectionEyebrow>
              <SectionCount>{unitObligations.length}</SectionCount>
            </SectionHead>
            {unitObligations.length === 0 ? (
              <SoftEmpty>No per-unit obligations apply to this unit. Everything is tracked building-wide below.</SoftEmpty>
            ) : (
              <Cards>
                {unitObligations.map(({ obligation, domain, entry }) => {
                  const state = entry.state || 'pending'
                  const sMeta = UNIT_STATE_META[state] || UNIT_STATE_META.pending
                  const SIcon = sMeta.Icon
                  const isFile = obligation.obligationKind === 'file_type'
                  const hasFile = !!(entry.fileId || entry.file_id)
                  const critLevel = critLevelOf(obligation)
                  const busy = reviewBusy === `${obligation.id}:${unitId}`
                  const expiry = entry.expiryDate || entry.expiry_date
                  return (
                    <Card key={obligation.id} $colour={sMeta.colour}>
                      <CardHead>
                        <CardName>{obligation.name}</CardName>
                        <StateBadge $colour={sMeta.colour}>
                          <SIcon style={{ fontSize: 13 }} /> {sMeta.label}
                        </StateBadge>
                      </CardHead>
                      <MetaLine>
                        <CritChip $tint={criticalityTint(obligation.criticalityWeight)} title={`Criticality: ${critLevel}`}>
                          {critLabelOf(obligation)}
                        </CritChip>
                        <ReqChip $tint={isFile ? '#7C5FE0' : '#c2410c'}>
                          {isFile ? 'Per-unit file' : 'Per-unit task'}
                        </ReqChip>
                        {domain?.name && <DomainChip>{domain.name}</DomainChip>}
                      </MetaLine>
                      {(hasFile || expiry) && (
                        <EvidenceLine>
                          {isFile
                            ? <InsertDriveFileRoundedIcon style={{ fontSize: 15 }} />
                            : <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} />}
                          <span>
                            {state === 'in_review' ? 'Submitted. Awaiting approval'
                              : state === 'covered' ? 'Evidence on file'
                                : state === 'expired' ? 'Evidence expired'
                                  : state === 'denied' ? 'Evidence denied'
                                    : 'No evidence yet'}
                            {expiry ? ` · valid until ${formatDate(expiry)}` : ''}
                          </span>
                        </EvidenceLine>
                      )}
                      {isFile && state === 'in_review' && hasFile && (
                        <ReviewBtns>
                          <ReviewBtn
                            $tone='ok'
                            disabled={busy}
                            onClick={() => approveUnit(obligation, entry)}
                            title='Approve this unit’s document. It counts towards the score straight away.'
                          >
                            <VerifiedRoundedIcon style={{ fontSize: 13 }} /> Approve
                          </ReviewBtn>
                          <ReviewBtn
                            $tone='bad'
                            disabled={busy}
                            onClick={() => { setDenyTarget({ obligation, entry }); setDenyReason('') }}
                            title='Deny this unit’s document. It stops counting and returns to awaiting evidence.'
                          >
                            <BlockRoundedIcon style={{ fontSize: 13 }} /> Deny
                          </ReviewBtn>
                        </ReviewBtns>
                      )}
                    </Card>
                  )
                })}
              </Cards>
            )}
          </Section>

          <Section>
            <SectionHead>
              <SectionEyebrow>Building-wide obligations</SectionEyebrow>
              <SectionCount>{buildingObligations.length}</SectionCount>
            </SectionHead>
            <SectionHint>
              These apply to the whole Building Passport. Including this unit. Manage them on the
              Building Passport’s compliance tracker.
            </SectionHint>
            {buildingObligations.length === 0 ? (
              <SoftEmpty>No building-wide obligations.</SoftEmpty>
            ) : (
              <BuildingList>
                {buildingObligations.map(({ obligation, domain }) => {
                  const status = obligation.status || 'pending'
                  const sMeta = STATUS_META[status] || STATUS_META.pending
                  const SIcon = sMeta.Icon
                  return (
                    <BuildingRow key={obligation.id} onClick={openBp} title='Manage on the Building Passport tracker'>
                      <BuildingMain>
                        <BuildingName>{obligation.name}</BuildingName>
                        <BuildingMeta>
                          <CritChip $tint={criticalityTint(obligation.criticalityWeight)}>
                            {critLabelOf(obligation)}
                          </CritChip>
                          {domain?.name && <DomainChip>{domain.name}</DomainChip>}
                        </BuildingMeta>
                      </BuildingMain>
                      <StateBadge $colour={sMeta.colour} $readonly>
                        <SIcon style={{ fontSize: 13 }} /> {sMeta.label}
                      </StateBadge>
                    </BuildingRow>
                  )
                })}
              </BuildingList>
            )}
          </Section>
        </>
      )}

      {denyTarget && (
        <ModalBackdrop onClick={() => setDenyTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>Deny unit evidence</ModalTitle>
            <ModalBody>
              The current document for <strong>{unitName || 'this unit'}</strong> will stop counting
              towards <strong>{denyTarget.obligation.name}</strong>, and this unit returns to awaiting
              evidence. You can give a brief reason.
            </ModalBody>
            <ReasonInput
              autoFocus
              placeholder='e.g. "Certificate covers the wrong flat"'
              value={denyReason}
              onChange={e => setDenyReason(e.target.value)}
              maxLength={1000}
            />
            <ModalActions>
              <SecondaryAction onClick={() => setDenyTarget(null)}>Cancel</SecondaryAction>
              <PrimaryAction onClick={() => {
                denyUnit(denyTarget.obligation, denyTarget.entry, denyReason.trim())
                setDenyTarget(null); setDenyReason('')
              }}>
                Deny evidence
              </PrimaryAction>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Wrap>
  )
}

export default UnitComplianceView

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0% { opacity: .55; } 50% { opacity: 1; } 100% { opacity: .55; }
`

const Wrap = styled.section`
  ${cardStyle}
  padding: 20px 22px;
  margin-bottom: 16px;
  animation: ${fadeUp} 320ms ease both;
`

const BackLink = styled(SharedBackLink)`
  margin-bottom: 12px;
`

const Header = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`

const HeaderLeft = styled.div`
  display: flex; flex-direction: column; gap: 4px; min-width: 0;
`

const Eyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Title = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.15;
`

const TitleSub = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
`

const HeaderRight = styled.div`
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
`

const ScorePill = styled.span`
  display: inline-flex; align-items: center;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint};
  padding: 3px 12px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`

const NotScoredPill = styled.span`
  display: inline-flex; align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 4px 12px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  border: 1px dashed ${line.strong};
`

const OpenBpBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: transparent;
  border: 1px solid ${line.strong};
  color: ${({ theme }) => theme.colors.haiti};
  padding: 6px 13px;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: ${transition};
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Section = styled.div`
  & + & { margin-top: 20px; }
`

const SectionHead = styled.div`
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 8px;
`

const SectionEyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionCount = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  border: 1px solid ${line.base};
  font-size: ${font.caption};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-variant-numeric: tabular-nums;
`

const SectionHint = styled.p`
  margin: 0 0 12px;
  font-size: ${font.small};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
`

const Card = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px 14px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-left: 3px solid ${({ $colour }) => $colour};
  border-radius: ${radius.md};
`

const CardHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
`

const CardName = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`

const StateBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  background: ${({ $colour }) => `${$colour}14`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
  font-size: ${font.caption};
  font-weight: 800;
  white-space: nowrap;
  cursor: ${({ $readonly }) => $readonly ? 'inherit' : 'default'};
`

const MetaLine = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
`

const CritChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 2px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  font-size: ${font.caption};
  font-weight: 800;
`

const ReqChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 2px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}12`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}30`};
  font-size: ${font.caption};
  font-weight: 700;
`

const DomainChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 2px 9px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px solid ${line.base};
  font-size: ${font.caption};
  font-weight: 600;
`

const EvidenceLine = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  svg { color: ${({ theme }) => theme.colors.grey[600]}; flex-shrink: 0; }
`

const ReviewBtns = styled.div`
  display: flex; gap: 8px;
`

const ReviewBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 12px;
  border-radius: ${radius.pill};
  border: 1px solid ${({ $tone }) => $tone === 'ok' ? `${RAG_LEVELS.low.fg}44` : `${RAG_LEVELS.critical.fg}44`};
  background: ${({ $tone }) => $tone === 'ok' ? `${RAG_LEVELS.low.fg}12` : `${RAG_LEVELS.critical.fg}12`};
  color: ${({ $tone }) => $tone === 'ok' ? RAG_LEVELS.low.fg : RAG_LEVELS.critical.fg};
  font-size: ${font.small};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover:not(:disabled) { background: ${({ $tone }) => $tone === 'ok' ? `${RAG_LEVELS.low.fg}20` : `${RAG_LEVELS.critical.fg}20`}; }
  &:disabled { opacity: 0.55; cursor: default; }
`

const BuildingList = styled.div`
  display: flex; flex-direction: column;
`

const BuildingRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 11px 12px;
  border-bottom: 1px solid ${surface.wash};
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${surface.wash}; }
  &:last-child { border-bottom: none; }
`

const BuildingMain = styled.div`
  display: flex; flex-direction: column; gap: 5px; min-width: 0;
`

const BuildingName = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const BuildingMeta = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
`

const Empty = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 36px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  line-height: 1.5;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`

const EmptyAction = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  color: #fff;
  padding: 7px 15px;
  border-radius: ${radius.pill};
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: filter 160ms ease;
  &:hover { filter: brightness(1.06); }
`

const SoftEmpty = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.small};
  background: ${surface.wash};
  border: 1px dashed ${line.base};
  border-radius: ${radius.sm};
`

const SkeletonWrap = styled.div`
  display: flex; flex-direction: column; gap: 20px;
`

const SkeletonSection = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`

const SkeletonCard = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px 14px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const SkeletonLine = styled.div`
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  border-radius: ${({ $r }) => $r || '4px'};
  background: ${line.strong};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  z-index: 100100;
  background: rgba(12, 18, 41, 0.42);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`

const ModalCard = styled.div`
  width: 100%; max-width: 440px;
  background: #fff;
  border-radius: ${radius.md};
  padding: 22px 24px;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const ModalTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const ModalBody = styled.p`
  margin: 0 0 14px;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ReasonInput = styled.textarea`
  width: 100%;
  min-height: 68px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font: inherit;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.secondary}; outline-offset: 1px; }
`

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 16px;
`

const SecondaryAction = styled.button`
  font: inherit;
  padding: 8px 16px;
  border-radius: ${radius.pill};
  border: 1px solid ${line.strong};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PrimaryAction = styled.button`
  font: inherit;
  padding: 8px 16px;
  border-radius: ${radius.pill};
  border: 1px solid ${RAG_LEVELS.critical.fg};
  background: ${RAG_LEVELS.critical.fg};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: filter 160ms ease;
  &:hover { filter: brightness(1.06); }
`
